// Modal "Templates do WhatsApp" (abre pelo menu do MessageInput em
// tickets/{id} quando a conexão é WhatsApp Oficial), no padrão Aeros Dark:
// casca AerosDialog + styles/aerosModalStyles. Duas etapas — lista de
// templates aprovados (busca + cards) e detalhe do selecionado (prévia do
// conteúdo + preenchimento das variáveis). A lógica de extração/substituição
// de variáveis é a mesma da versão anterior.
import React, { useState } from "react";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";
import IconButton from "@material-ui/core/IconButton";
import SearchIcon from "@material-ui/icons/Search";
import SendIcon from "@material-ui/icons/Send";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import DescriptionOutlinedIcon from "@material-ui/icons/DescriptionOutlined";
import { makeStyles, alpha } from "@material-ui/core/styles";

import AerosDialog from "../AerosDialog";
import useAerosModalStyles from "../../styles/aerosModalStyles";
import { AEROS, stateText } from "../../styles/aerosTokens";
import { i18n } from "../../translate/i18n";

const GROUP_LABELS = {
    header: "aeros.templateMeta.group.header",
    body: "aeros.templateMeta.group.body",
    footer: "aeros.templateMeta.group.footer",
    buttons: "aeros.templateMeta.group.buttons",
};

const useStyles = makeStyles((theme) => {
    const isLight = theme.palette.type === "light";
    const border = isLight ? theme.palette.divider : "rgba(255,255,255,0.07)";
    const purpleText = stateText(theme, "#C08BFF", "purple");
    const cyanText = stateText(theme, AEROS.cyan, "info");
    return {
        list: { display: "flex", flexDirection: "column", gap: 8 },
        card: {
            width: "100%",
            textAlign: "left",
            display: "flex",
            alignItems: "center",
            gap: 12,
            padding: "12px 14px",
            backgroundColor: theme.palette.aeros.surface,
            border: `1px solid ${border}`,
            borderRadius: 12,
            cursor: "pointer",
            font: "inherit",
            color: "inherit",
            transition: "border-color .15s, background-color .15s",
            "&:hover": {
                borderColor: alpha(theme.palette.primary.main, 0.5),
                backgroundColor: theme.palette.aeros.surface2,
            },
            "& .MuiSvgIcon-root": { flex: "none" },
        },
        cardTile: {
            width: 34,
            height: 34,
            borderRadius: 10,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flex: "none",
            backgroundColor: alpha(AEROS.cyan, 0.12),
            color: cyanText,
            "& .MuiSvgIcon-root": { fontSize: 18 },
        },
        cardMain: { minWidth: 0, flex: 1 },
        cardTitleRow: { display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" },
        cardTitle: {
            fontSize: 13,
            fontWeight: 700,
            color: theme.palette.text.primary,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
        },
        cardText: {
            fontSize: 11.5,
            color: theme.palette.text.secondary,
            marginTop: 4,
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
        },
        chevron: { color: theme.palette.aeros.textTertiary, fontSize: 20 },
        pillNeutral: {
            color: theme.palette.text.secondary,
            backgroundColor: isLight ? "rgba(15,20,40,0.05)" : "rgba(255,255,255,0.06)",
        },
        pillCategory: { color: purpleText, backgroundColor: alpha("#C08BFF", 0.12) },
        empty: {
            padding: "28px 16px",
            textAlign: "center",
            fontSize: 12.5,
            color: theme.palette.text.secondary,
        },
        previewBox: {
            padding: "12px 14px",
            fontSize: 12.5,
            lineHeight: 1.55,
            color: theme.palette.text.primary,
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
            backgroundColor: isLight ? "rgba(15,20,40,0.02)" : "rgba(255,255,255,0.02)",
        },
        groupTitle: {
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: "0.06em",
            textTransform: "uppercase",
            color: theme.palette.aeros.textTertiary,
        },
        group: { display: "flex", flexDirection: "column", gap: 10 },
        metaRow: { display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" },
    };
});

const TemplateModal = ({ open, handleClose, templates, onSelectTemplate }) => {
    const m = useAerosModalStyles();
    const classes = useStyles();
    const [search, setSearch] = useState('');
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [variables, setVariables] = useState([]);
    const [variableValues, setVariableValues] = useState({});
    const [renderedContent, setRenderedContent] = useState('');

    const handleSearchChange = (event) => {
        setSearch(event.target.value);
    };

    const extractVariablesByComponent = (components) => {
        // Numerada ({{1}}) ou nomeada ({{name}}, padrao do painel novo da
        // Meta). Com \d+ o template nomeado nao gerava input nenhum, o
        // backend mandava o template sem componentes e a Meta respondia
        // (#132000) Number of parameters does not match.
        const regex = /{{\s*([^{}\s]+)\s*}}/g;
        let variables = {
            header: [],
            body: [],
            footer: [],
            buttons: []
        };

        components.forEach((component) => {
            let match;
            const type = component.type?.toLowerCase();
            const text = component.text || '';

            if (type === 'header' && ['IMAGE', 'VIDEO'].includes(component?.format)) {
                variables[type].push({
                    type: `${component?.format.toLowerCase()}`,
                    prompt: component?.format.toLowerCase() === 'image'
                        ? i18n.t("aeros.templateMeta.imageUrl")
                        : i18n.t("aeros.templateMeta.videoUrl"),
                });
            }
            // Para os botões, verifique a URL para variáveis
            if (type === 'buttons') {
                const buttons = JSON.parse(component.buttons)
                buttons.forEach((button, index) => {
                    if (button.example) {
                        variables[type].push({ type: button.type, prompt: button.example, index: index });
                    }
                    else if (variables[type]) {
                        while ((match = regex.exec(text)) !== null) {
                            variables[type].push(match[0]);
                        }
                    }
                });
            } else if (variables[type]) {
                while ((match = regex.exec(text)) !== null) {
                    variables[type].push({ type: 'text', prompt: match[0] });
                }
            } else {
                console.warn(`Tipo de componente desconhecido: ${type}`);
            }
        });
        return variables;
    };

    const handleSelectTemplate = (template) => {
        const components = template?.components || [];
        const extractedVariables = extractVariablesByComponent(components);

        setSelectedTemplate(template);
        setVariables(extractedVariables);
        setRenderedContent(components.map((component) => component?.text).join(`\n`));
    };

    const voltarParaLista = () => {
        setSelectedTemplate(null);
        setVariables([]);
        setVariableValues({});
        setRenderedContent('');
    };

    const fecharModal = () => {
        voltarParaLista();
        setSearch('');
        handleClose();
    };

    // Token real da variavel ({{name}} em template nomeado, {{1}} em
    // numerado); o indice sozinho gravaria "{{name}}" literal no historico.
    const placeholderDe = (componentType, index) => {
        const prompt = variables[componentType]?.[index]?.prompt;
        return typeof prompt === 'string' && prompt.startsWith('{{') ? prompt : `{{${index + 1}}}`;
    };

    const generateBodyToSave = (content, valores) => {
        let bodyToSave = content;
        Object.keys(valores).forEach((componentType) => {
            const componentVariables = valores[componentType];

            if (Array.isArray(componentVariables)) {
                // Se for um array, iteramos sobre ele substituindo as variáveis
                componentVariables.forEach((variable, index) => {
                    const placeholder = placeholderDe(componentType, index);
                    const value = variable?.value || '';
                    bodyToSave = bodyToSave.replace(placeholder, value);
                });
            } else if (typeof componentVariables === 'object') {
                // Se for um objeto (caso do header com uma imagem ou outro tipo de conteúdo)
                if (componentType === 'header') {
                    // Substituir um placeholder específico no header por uma URL de imagem
                    Object.keys(componentVariables).forEach((key, index) => {
                        const value = componentVariables[key]?.value || '';
                        if (value.startsWith("http")) {
                            bodyToSave = `${value} \n ${bodyToSave}`
                        } else {
                            const placeholder = placeholderDe(componentType, index);

                            bodyToSave = bodyToSave.replace(placeholder, value);
                        }
                    });
                } else {
                    // Para texto ou outros tipos de componentes, substituímos as variáveis como antes
                    Object.keys(componentVariables).forEach((key, index) => {
                        const placeholder = placeholderDe(componentType, index);
                        const value = componentVariables[key]?.value || '';
                        bodyToSave = bodyToSave.replace(placeholder, value);
                    });
                }
            } else {
                console.error(`Expected array or object for componentType: ${componentType}, but got`, componentVariables);
            }
        });

        return bodyToSave;
    };

    const gruposComVariaveis = Object.keys(variables).filter(
        (componentType) => variables[componentType].length > 0
    );
    const totalVariaveis = gruposComVariaveis.reduce(
        (total, componentType) => total + variables[componentType].length,
        0
    );
    const variavelPreenchida = (componentType, index) =>
        !!(variableValues[componentType]?.[index]?.value || '').trim();
    const preenchidas = gruposComVariaveis.reduce(
        (total, componentType) =>
            total + variables[componentType].filter((_, index) => variavelPreenchida(componentType, index)).length,
        0
    );
    const faltaPreencherVariavel = preenchidas < totalVariaveis;

    const handleSendTemplate = () => {
        if (faltaPreencherVariavel) return;
        const bodyToSave = generateBodyToSave(renderedContent, variableValues);
        const templateWithVariables = {
            ...selectedTemplate,
            variables: variableValues,
            bodyToSave
        };
        onSelectTemplate(templateWithVariables);
    };

    const handleVariableChange = (componentType, index, value, buttonIndex) => {
        // Atualiza o estado das variáveis por tipo de componente
        const newComponentValues = { ...variableValues[componentType], [index]: { value, buttonIndex } };
        const newValues = { ...variableValues, [componentType]: newComponentValues };
        setVariableValues(newValues);
    };

    const busca = (search || '').toLowerCase();
    const filteredTemplates = (templates || []).filter((template) =>
        (template?.shortcode || '').toLowerCase().includes(busca)
    );

    const conteudoDe = (template) =>
        (template?.components || [])
            .map((component) => component?.text)
            .filter(Boolean)
            .join(`\n`);

    return (
        <AerosDialog
            open={open}
            onClose={fecharModal}
            width={640}
            title={i18n.t("aeros.templateMeta.title")}
            subtitle={
                selectedTemplate
                    ? selectedTemplate.shortcode || selectedTemplate.name
                    : i18n.t("aeros.templateMeta.subtitle", { count: (templates || []).length })
            }
            headerIcon={
                selectedTemplate ? (
                    <IconButton
                        size="small"
                        onClick={voltarParaLista}
                        aria-label={i18n.t("aeros.templateMeta.back")}
                    >
                        <ArrowBackIcon fontSize="small" />
                    </IconButton>
                ) : undefined
            }
            onSubmit={selectedTemplate ? handleSendTemplate : undefined}
            submitLabel={i18n.t("aeros.templateMeta.send")}
            submitIcon={<SendIcon />}
            submitDisabled={faltaPreencherVariavel}
            footerLeft={
                selectedTemplate && totalVariaveis > 0 ? (
                    <span className={m.hint}>
                        {i18n.t("aeros.templateMeta.filled", {
                            filled: preenchidas,
                            total: totalVariaveis,
                        })}
                    </span>
                ) : undefined
            }
        >
            {!selectedTemplate && (
                <>
                    <TextField
                        variant="outlined"
                        placeholder={i18n.t("aeros.templateMeta.search")}
                        value={search}
                        onChange={handleSearchChange}
                        className={m.input}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon />
                                </InputAdornment>
                            ),
                        }}
                    />

                    {filteredTemplates.length === 0 ? (
                        <div className={classes.empty}>
                            {(templates || []).length === 0
                                ? i18n.t("aeros.templateMeta.empty")
                                : i18n.t("aeros.templateMeta.emptySearch")}
                        </div>
                    ) : (
                        <div className={classes.list}>
                            {filteredTemplates.map((template, index) => (
                                <button
                                    type="button"
                                    key={template.id || index}
                                    className={classes.card}
                                    onClick={() => handleSelectTemplate(template)}
                                >
                                    <span className={classes.cardTile}>
                                        <DescriptionOutlinedIcon />
                                    </span>
                                    <span className={classes.cardMain}>
                                        <span className={classes.cardTitleRow}>
                                            <span className={classes.cardTitle}>{template.shortcode}</span>
                                            {template.language && (
                                                <span className={`${m.pill} ${classes.pillNeutral}`}>
                                                    {template.language}
                                                </span>
                                            )}
                                            {template.category && (
                                                <span className={`${m.pill} ${classes.pillCategory}`}>
                                                    {template.category}
                                                </span>
                                            )}
                                        </span>
                                        <span className={classes.cardText}>
                                            {conteudoDe(template) || i18n.t("aeros.templateMeta.noContent")}
                                        </span>
                                    </span>
                                    <ChevronRightIcon className={classes.chevron} />
                                </button>
                            ))}
                        </div>
                    )}
                </>
            )}

            {selectedTemplate && (
                <>
                    <div className={classes.metaRow}>
                        {selectedTemplate.language && (
                            <span className={`${m.pill} ${classes.pillNeutral}`}>
                                {selectedTemplate.language}
                            </span>
                        )}
                        {selectedTemplate.category && (
                            <span className={`${m.pill} ${classes.pillCategory}`}>
                                {selectedTemplate.category}
                            </span>
                        )}
                    </div>

                    <div className={m.section}>
                        <div className={m.sectionHead}>{i18n.t("aeros.templateMeta.preview")}</div>
                        <div className={classes.previewBox}>
                            {renderedContent || i18n.t("aeros.templateMeta.noContent")}
                        </div>
                    </div>

                    {totalVariaveis === 0 ? (
                        <div className={m.hint}>{i18n.t("aeros.templateMeta.noVariables")}</div>
                    ) : (
                        gruposComVariaveis.map((componentType) => (
                            <div key={componentType} className={classes.group}>
                                <div className={classes.groupTitle}>
                                    {i18n.t(GROUP_LABELS[componentType] || componentType)}
                                </div>
                                {variables[componentType].map((variable, index) => (
                                    <div key={`${componentType}-${index}`}>
                                        <label className={m.fieldLabel}>{`${variable?.prompt}`}</label>
                                        <TextField
                                            variant="outlined"
                                            className={m.input}
                                            placeholder={i18n.t("aeros.templateMeta.valuePlaceholder")}
                                            value={variableValues[componentType]?.[index]?.value || ''}
                                            onChange={(e) =>
                                                handleVariableChange(
                                                    componentType,
                                                    index,
                                                    e.target.value,
                                                    variable?.index || 0
                                                )
                                            }
                                        />
                                    </div>
                                ))}
                            </div>
                        ))
                    )}
                </>
            )}
        </AerosDialog>
    );
};

export default TemplateModal;
